// Merging and totalling a wallet's Drip positions for the unified position card.
//
// Two reads describe the same wallet: useDripPositions covers every vault, and
// useDripPosition adds USD, the epoch countdown and resolved reward-token
// metadata for the deposit-selected vault only. The card shows one list, so the
// reconciliation lives here as pure functions rather than in the page's JSX.

import { DripPosition } from "./dripFetch";

export interface DripPositionsSummary {
  /** Summed USD value of the positions that could be priced; null when none could. */
  totalUsd: number | null;
  /**
   * Positions left out of `totalUsd` because they carry no price. Non-zero means
   * the total is partial and must be labelled as such — silently under-reporting
   * a wallet's value is worse than admitting the gap.
   */
  unpricedCount: number;
  /** Epoch countdown; shared by every vault, so the first one that carries it wins. */
  nextEpochEstimateSeconds: number | null;
  /** True when any position is still warming up, which changes the tooltip copy. */
  isWarmingUp: boolean;
}

/**
 * The all-vaults list with the enriched single-vault read folded in.
 *
 * Registry order is preserved so the list does not reshuffle when the user
 * switches deposit asset. The enriched read wins field by field, except for the
 * two nullable enrichment fields: those only ever add information, so a null
 * there must not clobber a value the list read already resolved.
 */
export const mergeDripPositions = (
  positions: DripPosition[],
  enriched: DripPosition | null
): DripPosition[] => {
  if (!enriched) return positions;

  const index = positions.findIndex((p) => p.vaultId === enriched.vaultId);
  // The list read can still be in flight, or can have filtered out a position
  // the enriched read sees; either way the wallet holds it, so show it.
  if (index === -1) return [...positions, enriched];

  const merged = positions.slice();
  merged[index] = {
    ...positions[index],
    ...enriched,
    tokenValueUsd: enriched.tokenValueUsd ?? positions[index].tokenValueUsd,
    nextEpochEstimateSeconds:
      enriched.nextEpochEstimateSeconds ?? positions[index].nextEpochEstimateSeconds,
  };
  return merged;
};

/** Card-level figures derived from the merged list. */
export const summariseDripPositions = (
  positions: DripPosition[]
): DripPositionsSummary => {
  // An unavailable share supply leaves tokenValue at 0, which must never be
  // priced or summed: "~$0.00" would read as an empty position.
  const priced = positions.filter(
    (p) => p.tokenValueUsd !== null && !p.isValueUnavailable
  );

  return {
    totalUsd:
      priced.length > 0
        ? priced.reduce((sum, p) => sum + (p.tokenValueUsd ?? 0), 0)
        : null,
    unpricedCount: positions.length - priced.length,
    nextEpochEstimateSeconds:
      positions.find((p) => p.nextEpochEstimateSeconds !== null)
        ?.nextEpochEstimateSeconds ?? null,
    isWarmingUp: positions.some((p) => p.isWarmingUp),
  };
};
